"use client";

import * as React from "react";
import Link from "next/link";
import {
  usePathname,
  useSearchParams,
} from "next/navigation";
import {
  Award,
  BadgeCheck,
  ChevronDown,
  ChevronsUpDown,
  CreditCard,
  Home,
  Layers,
  Link2,
  Mail,
  MailPlus,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  ScrollText,
  Users,
  Wallet,
  LogOut,
  Building2,
  UserRound,
} from "lucide-react";
import {
  Button,
} from "@/components/ui/button";
import {
  Badge,
} from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  cn,
} from "@/lib/utils";

import { tintLabel } from "@/lib/tint";
import { BrandMark } from "@/components/brand-mark";

/**
 * The brand mark: the Sigalmark hexagon on the accent chip.
 *
 * It used to render /swm_white_small.png — a TENANT's logo (Sustainability West
 * Midlands), not ours. The real asset is olive-on-transparent and vanished
 * against the olive sidebar, so <BrandMark> masks it and paints it with
 * currentColor instead, giving constant contrast on the chip in every theme.
 */
function SigalmarkMark({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center overflow-hidden bg-accent",
        className,
      )}
    >
      <BrandMark className="size-[68%] text-accent-foreground" />
    </span>
  );
}

export type Role = "OPERATOR" | "ADMIN" | "USER";



export type Tenant = {
  id: string;
  name: string;
  initials: string;
  role: Role;
  /** Tenant brand tint — chip only. Never feeds layout or state logic. */
  tint?: string;
};

export type SessionUser = {
  name: string;
  email: string;
  initials: string;
};

type Icon = React.ComponentType<{ className?: string }>;

/** `tour` marks the element for the guided tour (data-tour="..."). */
type NavLeaf = { label: string; href: string; roles?: Role[]; tour?: string };
type NavItem = {
  label: string;
  href: string;
  icon: Icon;
  children?: NavLeaf[];
  tour?: string;
};
type NavGroup = {
  key: string;
  label: string;
  /** When true the heading renders the live tenant name. */
  tenantHeading?: boolean;
  roles: Role[];
  items: NavItem[];
};

export const NAV_GROUPS: NavGroup[] = [
  {
    key: "workspace",
    label: "Workspace",
    roles: ["OPERATOR", "ADMIN", "USER"],
    items: [
      { label: "Overview", href: "/", icon: Home },
      {
        label: "Certificates",
        href: "/certificates",
        icon: Award,
        tour: "sidebar-certificates",
        children: [
          { label: "All", href: "/certificates", tour: "sidebar-all" },
          {
            label: "Issue",
            href: "/certificates?tab=create",
            roles: ["ADMIN", "OPERATOR"],
            tour: "sidebar-issue",
          },
          { label: "Analytics", href: "/certificates?tab=stats", tour: "sidebar-analytics" },
          { label: "Verify", href: "/verify", tour: "sidebar-verify" },
        ],
      },
    ],
  },
  {
    key: "manage",
    label: "Manage",
    tenantHeading: true,
    roles: ["OPERATOR", "ADMIN"],
    items: [
      { label: "Team", href: "/tenant/users", icon: Users },
      { label: "Invitations", href: "/invitations", icon: MailPlus },
      { label: "Plan & billing", href: "/tenant/subscription", icon: CreditCard },
      { label: "Branding", href: "/tenant/branding", icon: BadgeCheck },
      { label: "Email templates", href: "/tenant/email-templates", icon: Mail },
    ],
  },
  {
    key: "platform",
    label: "Platform",
    roles: ["OPERATOR"],
    items: [
      { label: "Tenants", href: "/admin/tenants", icon: Building2 },
      { label: "All users", href: "/admin/users", icon: Users },
      { label: "Contracts", href: "/admin/contracts", icon: ScrollText },
      { label: "Plans", href: "/admin/subscriptions", icon: Layers },
      { label: "Funding wallets", href: "/admin/master-wallets", icon: Wallet },
      { label: "Shortener domains", href: "/admin/shortener-domains", icon: Link2 },
    ],
  },
];

const COLLAPSE_KEY = "sigalmark.sidebar.collapsed";

function visibleGroups(role: Role) {
  return NAV_GROUPS.filter((g) => g.roles.includes(role)).map((g) => ({
    ...g,
    items: g.items.map((it) => ({
      ...it,
      children: it.children?.filter((c) => !c.roles || c.roles.includes(role)),
    })),
  }));
}

function useCurrentRoute() {
  const pathname = usePathname();
  const params = useSearchParams();
  const tab = params.get("tab");
  return tab ? `${pathname}?tab=${tab}` : pathname;
}

function groupForRoute(role: Role, route: string) {
  const match = visibleGroups(role).find((g) =>
    g.items.some(
      (it) => it.href === route || it.children?.some((c) => c.href === route)
    )
  );
  return match?.key ?? "workspace";
}

/** Persisted collapse state. */
export function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = React.useState(false);
  React.useEffect(() => {
    setCollapsed(window.localStorage.getItem(COLLAPSE_KEY) === "1");
  }, []);
  const toggle = React.useCallback(() => {
    setCollapsed((c) => {
      window.localStorage.setItem(COLLAPSE_KEY, c ? "0" : "1");
      return !c;
    });
  }, []);
  return { collapsed, toggle };
}

function NavRows({
  role,
  route,
  onNavigate,
  compact,
}: {
  role: Role;
  route: string;
  onNavigate?: () => void;
  /** Mobile drawer: 44px minimum rows. */
  compact?: boolean;
}) {
  // Accordion: exactly one group open. Defaults to the route's group.
  const [openKey, setOpenKey] = React.useState<string | null>(() =>
    groupForRoute(role, route)
  );
  React.useEffect(() => setOpenKey(groupForRoute(role, route)), [role, route]);

  const tenantHeadingName = React.useContext(TenantNameContext);

  return (
    <>
      {visibleGroups(role).map((g) => {
        const open = openKey === g.key;
        return (
          <div key={g.key} className="flex flex-col gap-0.5">
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpenKey(open ? null : g.key)}
              className="flex items-center gap-2 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-sidebar-accent/50"
            >
              <span className="flex-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-sidebar-foreground/60">
                {g.tenantHeading ? `Manage ${tenantHeadingName}` : g.label}
              </span>
              <ChevronDown
                className={cn(
                  "size-3.5 shrink-0 text-sidebar-foreground/60 transition-transform",
                  !open && "-rotate-90"
                )}
              />
            </button>

            {open && (
              <div className="flex flex-col gap-0.5 pb-1.5">
                {g.items.map((it) => {
                  const kids = it.children ?? [];
                  const childActive = kids.some((c) => c.href === route);
                  const active = it.href === route && !childActive;
                  const showKids = kids.length > 0 && (childActive || it.href === route);
                  return (
                    <div key={it.href} className="flex flex-col">
                      <Link
                        href={it.href}
                        onClick={onNavigate}
                        data-tour={it.tour}
                        aria-current={active ? "page" : undefined}
                        className={cn(
                          "flex items-center gap-2.5 rounded-md px-2.5 transition-colors",
                          compact ? "min-h-11 text-[15px]" : "py-2.5 text-sm",
                          active
                            ? "bg-primary font-semibold text-primary-foreground"
                            : "text-sidebar-foreground/85 hover:bg-sidebar-accent/50"
                        )}
                      >
                        <it.icon className="size-[17px] shrink-0" />
                        {/* whitespace-nowrap: labels never truncate at 248px */}
                        <span className="flex-1 whitespace-nowrap">{it.label}</span>
                        {kids.length > 0 && (
                          <ChevronDown
                            className={cn(
                              "size-3.5 shrink-0 opacity-70 transition-transform",
                              !showKids && "-rotate-90"
                            )}
                          />
                        )}
                      </Link>

                      {showKids && (
                        <div className="ml-[22px] mt-0.5 flex flex-col gap-0.5 border-l border-sidebar-border pl-3">
                          {kids.map((c) => (
                            <Link
                              key={c.href}
                              href={c.href}
                              onClick={onNavigate}
                              data-tour={c.tour}
                              aria-current={c.href === route ? "page" : undefined}
                              className={cn(
                                "flex items-center rounded-md px-2.5 transition-colors",
                                compact ? "min-h-11 text-sm" : "py-1.5 text-[13px]",
                                c.href === route
                                  ? "bg-primary font-semibold text-primary-foreground"
                                  : "text-sidebar-foreground/85 hover:bg-sidebar-accent/50"
                              )}
                            >
                              {c.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

const TenantNameContext = React.createContext<string>("");

function TenantSwitcher({
  tenant,
  tenants,
  onSwitchTenant,
  compact,
}: {
  tenant: Tenant;
  tenants: Tenant[];
  onSwitchTenant?: (id: string) => void;
  compact?: boolean;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex w-full items-center gap-2.5 rounded-[var(--radius)] border border-sidebar-border bg-sidebar-accent/40 px-2.5 text-left transition-colors hover:bg-sidebar-accent",
            compact ? "min-h-11" : "py-2.5"
          )}
        >
          <span
            className="flex size-6 shrink-0 items-center justify-center rounded-[7px] text-[10px] font-semibold"
            style={tintLabel(tenant.tint)}
          >
            {tenant.initials}
          </span>
          <span className="min-w-0 flex-1 truncate text-[13px] font-medium">
            {tenant.name}
          </span>
          <ChevronsUpDown className="size-3.5 shrink-0 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-60">
        <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          Switch tenant
        </DropdownMenuLabel>
        {tenants.map((t) => (
          <DropdownMenuItem key={t.id} onSelect={() => onSwitchTenant?.(t.id)} className="gap-2.5">
            <span
              className="flex size-[22px] shrink-0 items-center justify-center rounded-md text-[10px] font-semibold"
              style={tintLabel(t.tint)}
            >
              {t.initials}
            </span>
            <span className="flex-1 truncate text-[13px]">{t.name}</span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              {t.role.toLowerCase()}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SidebarFooter({
  tenant,
  tenants,
  user,
  role,
  onSwitchTenant,
  onSignOut,
  compact,
  themeSlot,
}: {
  tenant: Tenant;
  tenants: Tenant[];
  user: SessionUser;
  role: Role;
  onSwitchTenant?: (id: string) => void;
  onSignOut?: () => void;
  compact?: boolean;
  /** Injected theme control. Kept as a slot so this module stays presentational. */
  themeSlot?: React.ReactNode;
}) {
  return (
    <div className="flex shrink-0 flex-col gap-1.5 border-t border-sidebar-border p-2">
      {themeSlot ? (
        <div className="px-0.5 pb-0.5">{themeSlot}</div>
      ) : null}

      <TenantSwitcher
        tenant={tenant}
        tenants={tenants}
        onSwitchTenant={onSwitchTenant}
        compact={compact}
      />

      {/* The card is the account menu: it is the only route to /profile, which
          is where 2FA, personal details and the tutorial replay live. */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Account menu"
            className={cn(
              "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-sidebar-accent/50",
              compact && "min-h-11"
            )}
          >
            <span className="flex size-[30px] shrink-0 items-center justify-center rounded-full bg-accent text-xs font-semibold text-accent-foreground">
              {user.initials}
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-[13px] font-medium">{user.name}</span>
              <span className="truncate text-xs text-sidebar-foreground/70">{user.email}</span>
            </span>
            <Badge
              variant="outline"
              className="shrink-0 rounded-full border-sidebar-foreground/30 bg-sidebar-foreground/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-sidebar-foreground"
            >
              {role.toLowerCase()}
            </Badge>
            <ChevronsUpDown className="size-3.5 shrink-0 opacity-70" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side="top" className="w-60">
          <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Account
          </DropdownMenuLabel>
          <DropdownMenuItem asChild className="gap-2.5">
            <Link href="/profile" data-tour="user-profile">
              <UserRound className="size-4" />
              Profile &amp; security
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onSignOut?.()} className="gap-2.5">
            <LogOut className="size-4" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function CollapsedRail({
  role,
  route,
  tenant,
  user,
  onExpand,
  onSignOut,
}: {
  role: Role;
  route: string;
  tenant: Tenant;
  user: SessionUser;
  onExpand: () => void;
  onSignOut?: () => void;
}) {
  const groups = visibleGroups(role);
  return (
    <TooltipProvider delayDuration={120}>
      <aside className="flex h-dvh w-16 shrink-0 flex-col items-center bg-sidebar text-sidebar-foreground">
        <div className="flex w-full shrink-0 justify-center border-b border-sidebar-border py-4">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={onExpand}
                aria-label="Expand sidebar"
                className="flex size-[30px] items-center justify-center rounded-lg bg-accent text-accent-foreground"
              >
                <PanelLeftOpen className="size-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">Expand sidebar</TooltipContent>
          </Tooltip>
        </div>

        <nav className="flex w-full min-h-0 flex-1 flex-col items-center gap-1 overflow-y-auto py-3">
          {groups.map((g, gi) => (
            <React.Fragment key={g.key}>
              {g.items.map((it) => {
                const active =
                  it.href === route || it.children?.some((c) => c.href === route);
                return (
                  <Tooltip key={it.href}>
                    <TooltipTrigger asChild>
                      <Link
                        href={it.href}
                        aria-label={it.label}
                        className={cn(
                          "flex size-11 items-center justify-center rounded-[var(--radius)] transition-colors",
                          active
                            ? "bg-primary text-primary-foreground"
                            : "text-sidebar-foreground/85 hover:bg-sidebar-accent/50"
                        )}
                      >
                        <it.icon className="size-[18px]" />
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="right">{it.label}</TooltipContent>
                  </Tooltip>
                );
              })}
              {gi < groups.length - 1 && (
                <span className="my-1 h-px w-6 bg-sidebar-border" />
              )}
            </React.Fragment>
          ))}
        </nav>

        <div className="flex w-full shrink-0 flex-col items-center gap-2 border-t border-sidebar-border py-2.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={`Tenant: ${tenant.name}`}
                className="flex size-8 items-center justify-center rounded-[9px] text-[11px] font-semibold"
                style={tintLabel(tenant.tint)}
              >
                {tenant.initials}
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">{tenant.name}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                href="/profile"
                aria-label={`${user.name} - profile and security`}
                className="flex size-[30px] items-center justify-center rounded-full bg-accent text-[11px] font-semibold text-accent-foreground transition-opacity hover:opacity-90"
              >
                {user.initials}
              </Link>
            </TooltipTrigger>
            <TooltipContent side="right">
              {user.name} · {role.toLowerCase()} — profile &amp; security
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={onSignOut}
                aria-label="Sign out"
                className="flex size-9 items-center justify-center rounded-md text-sidebar-foreground/85 hover:bg-sidebar-accent/50"
              >
                <LogOut className="size-[18px]" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">Sign out</TooltipContent>
          </Tooltip>
        </div>
      </aside>
    </TooltipProvider>
  );
}

export function AppSidebar(props: {
  role: Role;
  tenant: Tenant;
  tenants: Tenant[];
  user: SessionUser;
  onSwitchTenant?: (id: string) => void;
  onSignOut?: () => void;
  themeSlot?: React.ReactNode;
}) {
  const { role, tenant, tenants, user, onSwitchTenant, onSignOut, themeSlot } = props;
  const route = useCurrentRoute();
  const { collapsed, toggle } = useSidebarCollapsed();

  if (collapsed) {
    return (
      <div className="max-md:hidden">
        <CollapsedRail
          role={role}
          route={route}
          tenant={tenant}
          user={user}
          onExpand={toggle}
          onSignOut={onSignOut}
        />
      </div>
    );
  }

  return (
    <TenantNameContext.Provider value={tenant.name}>
      <aside className="flex h-dvh w-[248px] shrink-0 flex-col bg-sidebar text-sidebar-foreground max-md:hidden">
        <div className="flex shrink-0 items-center gap-2.5 border-b border-sidebar-border p-4">
          <SigalmarkMark className="size-[30px] rounded-lg" />
          <span className="flex-1 text-[15px] font-semibold tracking-[-0.01em]">
            Sigalmark
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            aria-label="Collapse sidebar"
            className="size-7 text-sidebar-foreground/70 hover:bg-sidebar-accent"
          >
            <PanelLeftClose className="size-4" />
          </Button>
        </div>

        {/* the only scroll region in the sidebar */}
        <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-2">
          <NavRows role={role} route={route} />
        </nav>

        <SidebarFooter
          tenant={tenant}
          tenants={tenants}
          user={user}
          role={role}
          onSwitchTenant={onSwitchTenant}
          onSignOut={onSignOut}
          themeSlot={themeSlot}
        />
      </aside>
    </TenantNameContext.Provider>
  );
}

/** Compact top bar + slide-over drawer. Mobile only. */
export function MobileTopBar(props: {
  role: Role;
  tenant: Tenant;
  tenants: Tenant[];
  user: SessionUser;
  onSwitchTenant?: (id: string) => void;
  onSignOut?: () => void;
  themeSlot?: React.ReactNode;
}) {
  const { role, tenant, tenants, user, onSwitchTenant, onSignOut, themeSlot } = props;
  const route = useCurrentRoute();
  const [open, setOpen] = React.useState(false);

  return (
    <div className="sticky top-0 z-40 flex h-14 items-center gap-3 bg-sidebar px-3 text-sidebar-foreground md:hidden">
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Open navigation"
            className="size-11 text-sidebar-foreground hover:bg-sidebar-accent"
          >
            <Menu className="size-5" />
          </Button>
        </SheetTrigger>
        <SheetContent
          side="left"
          className="flex w-[288px] flex-col gap-0 border-0 bg-sidebar p-0 text-sidebar-foreground"
        >
          <SheetTitle className="flex shrink-0 items-center gap-2.5 border-b border-sidebar-border p-3 text-[15px] font-semibold text-sidebar-foreground">
            <SigalmarkMark className="size-[30px] rounded-lg" />
            Sigalmark
          </SheetTitle>
          <TenantNameContext.Provider value={tenant.name}>
            <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-2">
              <NavRows role={role} route={route} compact onNavigate={() => setOpen(false)} />
            </nav>
            <SidebarFooter
              tenant={tenant}
              tenants={tenants}
              user={user}
              role={role}
              onSwitchTenant={onSwitchTenant}
              onSignOut={onSignOut}
              compact
              themeSlot={themeSlot}
            />
          </TenantNameContext.Provider>
        </SheetContent>
      </Sheet>

      <span className="flex flex-1 items-center gap-2">
        <SigalmarkMark className="size-[22px] rounded-md" />
        <span className="text-[15px] font-semibold">Sigalmark</span>
      </span>

      <span className="flex size-[30px] items-center justify-center rounded-full bg-accent text-[11px] font-semibold text-accent-foreground">
        {user.initials}
      </span>
    </div>
  );
}

/** The one shell. `main` is the app's only scroll region. */
export function AppShell({
  children,
  ...session
}: {
  children: React.ReactNode;
  role: Role;
  tenant: Tenant;
  tenants: Tenant[];
  user: SessionUser;
  onSwitchTenant?: (id: string) => void;
  onSignOut?: () => void;
  themeSlot?: React.ReactNode;
}) {
  return (
    <div className="flex h-dvh bg-background max-md:flex-col">
      <AppSidebar {...session} />
      <MobileTopBar {...session} />
      <main className="min-w-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex max-w-[1200px] flex-col gap-6 px-8 py-8 max-md:px-4 max-md:py-6">
          {children}
        </div>
      </main>
    </div>
  );
}
